/**
 * Browser-safe HTTP transport for plugin client code.
 *
 * This module provides the low-level HTTP primitives that plugins can safely
 * depend on without pulling in host-specific API methods. It handles:
 * - Auth token and org ID headers
 * - Cross-origin credentials
 * - 401 handling (token clear + event dispatch)
 * - Error parsing into ApiError
 * - Empty response handling (204, content-length: 0)
 *
 * The transport itself is the published SDK (`@runloop/reflex-client`):
 * this module configures it once for the host app's session auth (bearer
 * token in localStorage, org from the URL slug, `auth:unauthorized` on 401)
 * and `request()` delegates to it. `ApiError` is the SDK's `ReflexApiError`
 * under its historical name, so `instanceof` checks keep working across
 * web and plugins.
 *
 * Plugins should use `request()` for typed fetch calls to their own routes,
 * and the helper functions (`getApiUrl`, `authHeaders`, etc.) when building
 * custom fetch logic (SSE, uploads, etc.).
 */

import { orgSlugFromPathname } from '@reflex/shared/routes';
import { configureReflex, reflexRequest, ReflexApiError } from '@runloop/reflex-client';
import type { ValidationIssue } from '@runloop/reflex-client';

type ImportMetaWithEnv = ImportMeta & { env?: { VITE_API_BASE_URL?: string } };

const BASE = (import.meta as ImportMetaWithEnv).env?.VITE_API_BASE_URL || '/api';

/**
 * True when the API base URL is absolute (starts with http), indicating
 * cross-origin requests that need credentials: 'include'.
 */
export const IS_CROSS_ORIGIN = BASE.startsWith('http');

const AUTH_TOKEN_KEY = 'auth_session_token';
const ACTIVE_ORG_KEY = 'active_organization_id';

/**
 * Build a full API URL from a relative path.
 */
export function getApiUrl(path: string): string {
  return `${BASE}${path}`;
}

/**
 * Get the active organization ID from localStorage.
 * Returns null if not set or localStorage is unavailable.
 */
export function getActiveOrganizationId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY);
  } catch {
    return null;
  }
}

/** Persist (or clear) the active organization fallback. */
export function setActiveOrganizationId(id: string | null): void {
  try {
    if (id) {
      localStorage.setItem(ACTIVE_ORG_KEY, id);
    } else {
      localStorage.removeItem(ACTIVE_ORG_KEY);
    }
  } catch {
    // Ignore unavailable storage in non-browser tests.
  }
}

/**
 * Read the active org identifier from `window.location.pathname` when the
 * SPA is on an org-scoped route (via the shared `orgSlugFromPathname`,
 * which the host app's `orgHeaders()` consults too). Returns `null` for
 * root-level routes and SSR/test environments. The server accepts a slug
 * or id for `x-organization-id`.
 */
export function getActiveOrgSlugFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return orgSlugFromPathname(window.location.pathname);
}

/**
 * Resolve the organization the host app should send to the server. The URL
 * slug is authoritative; localStorage is only a fallback for root-level
 * routes that do not carry an org.
 */
export function getActiveOrgPreference(): string | null {
  return getActiveOrgSlugFromUrl() ?? getActiveOrganizationId();
}

/**
 * Read the stored auth token from localStorage. Returns null when no
 * token is set or localStorage is unavailable.
 *
 * Most callers want {@link authHeaders} instead — `Authorization: Bearer
 * ...` is the canonical transport for HTTP. This exists for the handful
 * of transports that can't set custom headers: notably `new
 * WebSocket(...)` handshakes, which append the token as a `?token=`
 * query param the way the canonical `/api/ws` endpoint does (see
 * `auth-middleware.ts`).
 */
export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Persist the auth session token to localStorage. Password auth providers
 * (e.g. plugin-auth-email) call this after a successful login/registration so
 * the token returned by the server is sent as `Authorization: Bearer ...` on
 * subsequent {@link request} calls. Tolerates unavailable storage.
 */
export function setAuthToken(token: string): void {
  try {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch {
    // Ignore unavailable storage in non-browser tests.
  }
}

/**
 * Remove the stored auth session token from localStorage. Mirrors the cleanup
 * {@link request} performs on a 401; exposed for callers that need to drop the
 * session explicitly. Tolerates unavailable storage.
 */
export function clearAuthToken(): void {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // Ignore unavailable storage in non-browser tests.
  }
}

/**
 * One-shot flag set when a signed-in session stops being accepted, read by
 * the auth screen to explain why the visitor is looking at it. Lives in
 * sessionStorage so it survives the in-place swap to the login screen and a
 * reload, but not a new browser session.
 */
const SESSION_ENDED_KEY = 'auth_session_ended';

export function markSessionEnded(): void {
  try {
    sessionStorage.setItem(SESSION_ENDED_KEY, '1');
  } catch {
    // The notice is optional.
  }
}

export function takeSessionEndedSignal(): boolean {
  try {
    const ended = sessionStorage.getItem(SESSION_ENDED_KEY) === '1';
    sessionStorage.removeItem(SESSION_ENDED_KEY);
    return ended;
  } catch {
    return false;
  }
}

/**
 * Build authorization headers from the stored auth token.
 * Returns empty object if no token or localStorage is unavailable.
 */
export function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Build the `x-organization-id` header for the currently active org.
 * Prefers the URL slug (the source of truth — see `OrgSlugResolver` in
 * the host app) over the persisted localStorage value, so a hard load
 * of `/runloop/agents` is correctly scoped to `runloop` even when the
 * previous session left `org-alex` in localStorage. Returns `{}` when no
 * org is selected so callers can spread the result unconditionally.
 *
 * Any custom transport (AI SDK chat streams, SSE, uploads) that bypasses
 * `request()` must include this alongside `authHeaders()` or the server
 * falls back to the user's first membership.
 */
export function orgHeaders(override?: string | null): Record<string, string> {
  const id = override !== undefined ? override : getActiveOrgPreference();
  return id ? { 'x-organization-id': id } : {};
}

/**
 * Configure the SDK transport for the host app. The SDK appends `/api` to
 * its baseUrl, while `BASE` already carries it (default `'/api'`), so strip
 * the suffix and hand the SDK the bare origin ('' for same-origin builds).
 *
 * Runs once at module init: every configured value is a provider read per
 * request, so sign-in/sign-out and org switches are picked up live. jsdom
 * unit tests and Storybook are unaffected — the SDK calls the global
 * `fetch` at request time, which msw intercepts as before.
 */
const SDK_BASE_URL = BASE.endsWith('/api') ? BASE.slice(0, -'/api'.length) : BASE;

configureReflex({
  baseUrl: SDK_BASE_URL,
  timeoutMs: 30_000,
  getToken: getAuthToken,
  getOrganizationId: getActiveOrgPreference,
  ...(IS_CROSS_ORIGIN ? { credentials: 'include' as const } : {}),
  onUnauthorized: ({ path }) => {
    // A 401 from `/auth/*` is a failed sign-in attempt, not a dead session.
    if (path.startsWith('/auth/')) return;
    // A stored bearer the server just refused was a live session that
    // ended (expired, revoked, or the account was disabled). Remember that
    // so the login screen can say so instead of looking like a cold start.
    if (getAuthToken()) markSessionEnded();
    clearAuthToken();
    window.dispatchEvent(new Event('auth:unauthorized'));
  },
});

/**
 * One field-level validation problem reported by the server's canonical
 * `validation_error` envelope. `path` follows Zod's shape: an array of
 * object keys / array indices leading to the offending value.
 */
export type { ValidationIssue };

/**
 * Typed error class for non-2xx API responses.
 *
 * Contains the HTTP status, optional error code/hint from the server,
 * and the full response body for detailed error handling. For 400
 * responses with `error: 'validation_error'`, `issues` carries the
 * field-level breakdown emitted by the centralised handler in
 * `server/src/lib/middleware.ts`.
 *
 * This is the SDK's `ReflexApiError` re-exported under its historical
 * name: every error thrown by `request()` and the generated client is an
 * instance of this same class, so `err instanceof ApiError` keeps working.
 */
export { ReflexApiError as ApiError };

/**
 * Minimal Zod-like surface used by `validateOrThrow`. Accepts any object
 * with a `safeParse` method so we don't pin the helper to a specific Zod
 * major version — callers pass `z.object({...})` and TypeScript narrows
 * the return type. `path` is typed as `PropertyKey[]` (Zod's own shape) so
 * schemas with symbol keys still satisfy the interface; the helper
 * coerces symbols to strings on the way out.
 */
interface SafeParseIssue {
  path: PropertyKey[];
  message: string;
}
interface SafeParser<T> {
  safeParse(
    data: unknown,
  ): { success: true; data: T } | { success: false; error: { issues: SafeParseIssue[] } };
}

/**
 * Pre-send validation that mirrors the server's `validation_error`
 * envelope. Throws an `ApiError(400, 'validation_error', ...)` carrying
 * the same `issues` shape the centralised handler returns, so callers
 * can branch on `err.code === 'validation_error'` regardless of whether
 * the bad payload was caught in the browser or on the server.
 *
 * Use it in hooks before calling `request()` when a hook owns the
 * mutation payload (a la React Hook Form's `handleSubmit`) and the
 * schema is the source of truth.
 */
export function validateOrThrow<T>(schema: SafeParser<T>, payload: unknown): T {
  const result = schema.safeParse(payload);
  if (result.success) return result.data;
  const issues: ValidationIssue[] = result.error.issues.map((i) => ({
    path: i.path.map((p) => (typeof p === 'symbol' ? p.toString() : p)),
    message: i.message,
  }));
  const message = issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ');
  throw new ReflexApiError(
    message,
    400,
    'validation_error',
    undefined,
    { error: 'validation_error', message, issues },
    issues,
  );
}

/**
 * Options for {@link request}.
 */
export interface RequestOptions {
  // TODO(RUN-2582): resetAuthOn401 is a workaround, not the end state. It exists
  // because the web client owns the session token in localStorage and cannot tell
  // a session-expiry 401 from a connection-reauth 401, so it would otherwise sign
  // the user out on an expired Linear/Jira connection. Remove once the web client
  // moves to the server's httpOnly session cookie and stops owning session state.
  /**
   * Whether a 401 response should clear the stored auth token and dispatch the
   * global `auth:unauthorized` event (sign-out). A 401 on a normal route means
   * the user's *session* is invalid, so this defaults to `true`. Integration
   * routes can return 401 for a *connection-level* reauth (an expired
   * third-party OAuth token, surfaced as `code: 'reauth_required'`) that must
   * NOT sign the user out of the app — pass `false` so the 401 is surfaced as
   * an `ApiError` (branch on `err.code`) without touching the session.
   */
  resetAuthOn401?: boolean;
  /** Pin this request to an organization, or pass null to omit org scope. */
  organizationId?: string | null;
  /** Deadline covering both response headers and response-body consumption. */
  timeoutMs?: number;
  /** Override the Fetch API cache mode for this request. */
  cache?: RequestCache;
  /** Parse a successful response as JSON (default) or plain text. */
  responseType?: 'json' | 'text';
}

/**
 * Make an authenticated API request.
 *
 * Delegates to the SDK transport, which adds the auth and org headers from
 * the configuration above (explicit headers in `init` win). Handles:
 * - 401 responses: clears auth token, dispatches 'auth:unauthorized' event
 *   (unless `opts.resetAuthOn401 === false`)
 * - Non-2xx responses: throws ApiError with parsed body
 * - 204 / empty responses: returns undefined
 *
 * @param path - API path (e.g., '/users', '/agents/123')
 * @param init - Standard RequestInit options
 * @param opts - Request-level options (see {@link RequestOptions})
 * @returns Parsed JSON response, or undefined for empty responses
 * @throws ApiError for non-2xx responses
 */
export async function request<T>(
  path: string,
  init?: RequestInit,
  opts?: RequestOptions,
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  return reflexRequest<T>(
    path,
    {
      ...init,
      headers,
    },
    {
      notifyOnUnauthorized: opts?.resetAuthOn401,
      organizationId: opts?.organizationId,
      timeoutMs: opts?.timeoutMs,
      cache: opts?.cache,
      responseType: opts?.responseType,
    },
  );
}
